import { useEffect, useRef, useState } from 'react';
import { Map3D, Marker3D, useMap3D } from '@vis.gl/react-google-maps';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { buildTileUrl, tileAttribution } from '../../lib/tiles';
import { useVRAMState } from '../../lib/MapVRAMGuard';
import MapLayerToggle from './MapLayerToggle';
import VectorMap3D from './VectorMap3D';

/**
 * Hub3DMap — Photorealistic 3D Tiles map for HubDashboard.
 *
 * Props:
 *  - projects: list of { project_id, coords:[lat,lng], published, name, ... }
 *  - selectedId: string | null  — projet à survoler (déclenche flyCameraTo)
 *  - onMarkerClick: (project) => void  — clic sur un marker 3D
 *  - defaultCenter: { lat, lng }
 *  - apiKey: string
 *
 * Le composant expose en interne un FlyTo fluide via tilt+heading rotation
 * cinématique, ~1.8s. Dehors la zone Photorealistic Tiles, fallback satellite.
 */

// Fly cinématique vers selectedId avec rotation heading
const FlyToSelected = ({ selectedProject }) => {
  const map3d = useMap3D();
  const last = useRef(null);

  useEffect(() => {
    if (!map3d || !selectedProject) return;
    const c = selectedProject.coords;
    if (!Array.isArray(c) || c.length < 2) return;
    const key = `${selectedProject.project_id}`;
    if (last.current === key) return;
    last.current = key;

    try {
      if (typeof map3d.flyCameraTo === 'function') {
        // Phase 1 — plongée vers le bâtiment avec tilt 45° (cinématique)
        map3d.flyCameraTo({
          endCamera: {
            center: { lat: c[0], lng: c[1], altitude: 80 },
            tilt: 45,
            range: 600,
            heading: (selectedProject._heading_seed || 0) % 360,
          },
          durationMillis: 1800,
        });
        // Phase 2 — légère rotation orbitale après l'arrivée pour effet cinéma
        const t = setTimeout(() => {
          try {
            if (typeof map3d.flyCameraAround === 'function') {
              map3d.flyCameraAround({
                camera: {
                  center: { lat: c[0], lng: c[1], altitude: 80 },
                  tilt: 65,
                  range: 480,
                  heading: 0,
                },
                durationMillis: 8000,
                rounds: 0.25, // quart de tour subtil
              });
            }
          } catch {/* noop */}
        }, 1900);
        return () => clearTimeout(t);
      }
      // Fallback: direct setters
      map3d.center = { lat: c[0], lng: c[1], altitude: 80 };
      map3d.range = 480;
      map3d.tilt = 65;
    } catch {/* alpha API — swallow */}
  }, [map3d, selectedProject]);

  return null;
};

// Fly to a dropped pin (drop-pin flow from address search / map click)
const FlyToDropPin = ({ pin }) => {
  const map3d = useMap3D();
  const lastKey = useRef(null);
  useEffect(() => {
    if (!map3d || !pin) return;
    const key = `${pin.lat}|${pin.lon}`;
    if (lastKey.current === key) return;
    lastKey.current = key;
    try {
      if (typeof map3d.flyCameraTo === 'function') {
        map3d.flyCameraTo({
          endCamera: {
            center: { lat: pin.lat, lng: pin.lon, altitude: 0 },
            tilt: 60,
            range: 1500,
            heading: 0,
          },
          durationMillis: 1400,
        });
      }
    } catch {/* noop */}
  }, [map3d, pin]);
  return null;
};

// Adapt zoom-out to fit all pins. Refits when `nonce` changes (e.g., user clicks
// "Vue d'ensemble" or deselects a project from the right panel). Also runs once
// on initial mount (lastNonce starts at undefined so the first run is forced).
const FitAllOnLoad = ({ projects, nonce = 0 }) => {
  const map3d = useMap3D();
  const lastNonce = useRef(undefined);
  useEffect(() => {
    if (!map3d || !projects.length) return;
    if (lastNonce.current === nonce) return;
    lastNonce.current = nonce;
    // Vue orbitale globale sur le centroïde des projets
    const lats = projects.map(p => p.coords[0]);
    const lngs = projects.map(p => p.coords[1]);
    const cLat = (Math.min(...lats) + Math.max(...lats)) / 2;
    const cLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
    // Pick a range based on bbox span (rough heuristic) to actually frame all pins
    const span = Math.max(
      Math.max(...lats) - Math.min(...lats),
      Math.max(...lngs) - Math.min(...lngs),
    );
    const range = Math.max(800_000, Math.min(8_000_000, span * 120_000));
    try {
      if (typeof map3d.flyCameraTo === 'function') {
        map3d.flyCameraTo({
          endCamera: {
            center: { lat: cLat, lng: cLng, altitude: 0 },
            tilt: 45,
            range,
            heading: 0,
          },
          durationMillis: 1400,
        });
      }
    } catch {/* noop */}
  }, [map3d, projects, nonce]);
  return null;
};

// Fly to an address result (from top-bar geocoder search)
const FlyToAddress = ({ target }) => {
  const map3d = useMap3D();
  const lastKey = useRef(null);
  useEffect(() => {
    if (!map3d || !target || !target.lat || !target.lon) return;
    const key = `${target.lat}|${target.lon}|${target.nonce || ''}`;
    if (lastKey.current === key) return;
    lastKey.current = key;
    try {
      if (typeof map3d.flyCameraTo === 'function') {
        map3d.flyCameraTo({
          endCamera: {
            center: { lat: target.lat, lng: target.lon, altitude: target.altitude ?? 0 },
            tilt: target.tilt ?? 55,
            range: target.range ?? 4000,
            heading: target.heading ?? 0,
          },
          durationMillis: target.durationMillis ?? 1500,
        });
      }
    } catch {/* noop */}
  }, [map3d, target]);
  return null;
};

const Hub3DMap = ({
  projects = [],
  selectedId = null,
  onMarkerClick,
  defaultCenter = { lat: 46.6, lng: 2.45 },  // France centroid
  apiKey,
  onApiError,
  fitAllNonce = 0,
  flyTo = null,
  onNavigateDetails,
  onNavigateMinisite,
  dropPin = null,            // { lat, lon, display_name } — temporary location pin
  onMapClick,                // (lat, lng) => void — fired on empty-map click (repositions drop pin)
  cadastralPolygon = null,   // Phase 2 — OSM building footprint to highlight via Deck.gl
  cadastralHeight = null,    // Phase 2 — meters; drives extrusion height when inspection mode is on
  inspectionMode = false,    // Phase 2 — toggles extruded volumetric mode for the footprint
  cadastreParcels = null,    // Phase 3.A — list of {parcelle_id, polygon, ...}
  selectedParcelleId = null, // Phase 3.A — highlighted parcel id
  onParcelClick = null,      // Phase 3.A — clicking a parcel triggers selection
  planFootprints = null,     // Sprint 3.B P2 — list of {project_id, polygon_latlon, ...}
  onFootprintClick = null,   // Sprint 3.B P2 — clicking a footprint selects the project
}) => {
  const [loadError, setLoadError] = useState(null);
  const [errorReason, setErrorReason] = useState(null); // 'referrer' | 'quota' | 'tiles_disabled' | 'auth' | 'timeout'
  const wrapperRef = useRef(null);
  const pinned = projects.filter(p => Array.isArray(p.coords) && p.coords.length === 2);
  const selectedProject = pinned.find(p => p.project_id === selectedId);

  // ---- Detect Google Maps auth failures (e.g., referrer-restricted, billing/quota,
  //      Map Tiles API not enabled). These are runtime errors that bypass APIProvider.onError.
  //      We listen to `window.gm_authFailure` (called by the Maps JS SDK) and flip to 2D fallback. ----
  useEffect(() => {
    if (!apiKey) return;
    const prev = window.gm_authFailure;
    window.gm_authFailure = () => {
      setLoadError('auth_failure');
      setErrorReason((r) => r || 'auth');
      onApiError?.({ message: 'gm_authFailure' });
      if (typeof prev === 'function') try { prev(); } catch {/* noop */}
    };
    return () => { window.gm_authFailure = prev; };
  }, [apiKey, onApiError]);

  // ---- Intercept console.error to identify the *exact* Google Maps failure
  //      (RefererNotAllowedMapError, ApiTargetBlockedMapError, etc.) so the fallback
  //      banner can give the user actionable guidance instead of a generic error. ----
  useEffect(() => {
    if (!apiKey) return;
    const original = window.console.error;
    window.console.error = (...args) => {
      try {
        const text = args.map((a) => (typeof a === 'string' ? a : '')).join(' ');
        if (text.includes('Google Maps') || text.includes('RefererNotAllowed') || text.includes('ApiTargetBlocked') || text.includes('Map Tiles')) {
          if (text.includes('RefererNotAllowed')) setErrorReason((r) => r || 'referrer');
          else if (text.includes('ApiTargetBlocked') || text.includes('Map Tiles')) setErrorReason((r) => r || 'tiles_disabled');
          else if (text.includes('OverQuota') || text.includes('Quota')) setErrorReason((r) => r || 'quota');
          else setErrorReason((r) => r || 'auth');
        }
      } catch {/* noop */}
      return original.apply(window.console, args);
    };
    return () => { window.console.error = original; };
  }, [apiKey]);

  // ---- Sprint Layers (Feb 2026) — layer toggle + VRAM guard ----
  // Hooks MUST be declared before any early return (rules-of-hooks).
  // Defaults to 'vector' to be VRAM-safe at load. User-selected layer is
  // persisted in localStorage so the preference survives reloads.
  const VECTOR_MAP_ID = process.env.REACT_APP_GOOGLE_VECTOR_MAP_ID;
  const [layerPref, setLayerPref] = useState(() => {
    try { return window.localStorage.getItem('hub3dmap.layer') || 'vector'; } catch { return 'vector'; }
  });
  const [photorealFailed, setPhotorealFailed] = useState(false);
  const vramState = useVRAMState();

  // Photoreal-only health check : if the gmp-map-3d custom element doesn't render a
  // child canvas/iframe within 5s, assume the 3D tiles failed (quota / Map Tiles API
  // disabled / referrer restriction) and auto-revert to Vector layer (which still
  // benefits from the Google Maps key and stays GIS-capable). Leaflet fallback is
  // reserved for the worst case (no apiKey at all).
  useEffect(() => {
    if (!apiKey || loadError) return;
    const lockedByVRAMNow = vramState.hasReservations;
    const effective = lockedByVRAMNow ? 'vector' : layerPref;
    if (effective !== 'photoreal') return;
    const t = setTimeout(() => {
      const wrapper = wrapperRef.current;
      if (!wrapper) return;
      const map3dEl = wrapper.querySelector('gmp-map-3d');
      const hasCanvas = map3dEl && (map3dEl.shadowRoot?.querySelector('canvas') || map3dEl.querySelector('canvas'));
      if (!hasCanvas) {
        // Auto-downgrade to Vector layer instead of Leaflet — keeps the user
        // inside the Google Maps experience and shows a banner explaining why.
        setLayerPref('vector');
        try { window.localStorage.setItem('hub3dmap.layer', 'vector'); } catch {/* noop */}
        setPhotorealFailed(true);
        onApiError?.({ message: '3D Tiles unavailable — auto-reverted to Vector layer' });
      }
    }, 5000);
    return () => clearTimeout(t);
  }, [apiKey, loadError, onApiError, layerPref, vramState.hasReservations]);

  // ---- Fallback 2D (Leaflet) when Google Maps API key is missing OR auth failed ----
  if (!apiKey || loadError) {
    return <Hub2DFallback
      pinned={pinned}
      selectedProject={selectedProject}
      defaultCenter={defaultCenter}
      onMarkerClick={onMarkerClick}
      degraded={!!loadError}
      degradedReason={errorReason}
      fitAllNonce={fitAllNonce}
      flyTo={flyTo}
      onNavigateDetails={onNavigateDetails}
      onNavigateMinisite={onNavigateMinisite}
      dropPin={dropPin}
      onMapClick={onMapClick}
    />;
  }

  const lockedByVRAM = vramState.hasReservations;
  const lockedReason = lockedByVRAM ? vramState.list().join(', ') : null;
  // Effective layer = user preference UNLESS another heavy-WebGL module is
  // active, in which case we force vector (auto-downgrade). If the user
  // picked vector but no mapId is configured, we don't fall through to
  // Leaflet (that's reserved for *failure*), we render Vector without mapId
  // (2D-classical) and surface a warning in the toggle.
  const effectiveLayer = lockedByVRAM ? 'vector' : layerPref;
  const missingMapId = effectiveLayer === 'vector' && !VECTOR_MAP_ID;

  const onLayerChange = (next) => {
    // Reset the photoreal-failed flag when user actively tries again (so the
    // health check can re-run and either succeed or revert again gracefully).
    if (next === 'photoreal' && photorealFailed) setPhotorealFailed(false);
    setLayerPref(next);
    try { window.localStorage.setItem('hub3dmap.layer', next); } catch {/* noop */}
  };

  // Common props shared by Vector and Photoreal renderers
  const commonProps = {
    apiKey,
    projects: pinned,
    selectedId,
    onMarkerClick,
    defaultCenter,
    fitAllNonce,
    flyTo,
    dropPin,
    onMapClick,
    cadastralPolygon,
    cadastralHeight,
    inspectionMode,
    cadastreParcels,
    selectedParcelleId,
    onParcelClick,
    planFootprints,
    onFootprintClick,
  };

  return (
    <div className="relative w-full h-full" data-testid="hub-3d-map" ref={wrapperRef}>
      {effectiveLayer === 'photoreal' ? (
        <Map3D
            mode="HYBRID"
            defaultCenter={{ lat: defaultCenter.lat, lng: defaultCenter.lng, altitude: 0 }}
            defaultRange={1_500_000}
            defaultTilt={45}
            defaultHeading={0}
            style={{ width: '100%', height: '100%' }}
            data-testid="hub-3d-canvas"
            onClick={(ev) => {
              const pos = ev?.detail?.position || ev?.position;
              if (pos && typeof pos.lat === 'number' && typeof pos.lng === 'number') {
                onMapClick?.(pos.lat, pos.lng);
              }
            }}
          >
            <FitAllOnLoad projects={pinned} nonce={fitAllNonce} />
            <FlyToSelected selectedProject={selectedProject} />
            <FlyToAddress target={flyTo} />
            <FlyToDropPin pin={dropPin} />
            {pinned.map((p) => (
              <Marker3D
                key={p.project_id}
                position={{ lat: p.coords[0], lng: p.coords[1], altitude: 30 }}
                altitudeMode="RELATIVE_TO_GROUND"
                extruded
                onClick={() => onMarkerClick?.(p)}
              />
            ))}
            {dropPin && (
              <Marker3D
                key="hub-droppin"
                position={{ lat: dropPin.lat, lng: dropPin.lon, altitude: 60 }}
                altitudeMode="RELATIVE_TO_GROUND"
                extruded
              />
            )}
          </Map3D>
      ) : (
        // Layer 1 — Vector 3D (or 2D classical if mapId missing)
        <VectorMap3D
          {...commonProps}
          mapId={VECTOR_MAP_ID}
        />
      )}

      <MapLayerToggle
        value={effectiveLayer}
        onChange={onLayerChange}
        locked={lockedByVRAM}
        lockedReason={lockedReason}
        missingMapId={missingMapId}
        photorealFailed={photorealFailed}
      />
    </div>
  );
};

// ============================================================================
// 2D Leaflet fallback — used when REACT_APP_GOOGLE_MAPS_API_KEY is absent
// ============================================================================

const goldPin = L.divIcon({
  className: '',
  iconSize: [22, 22],
  iconAnchor: [11, 11],
  html: `
    <div style="position:relative;width:22px;height:22px;">
      <div style="position:absolute;inset:0;border-radius:50%;background:rgba(217,160,91,0.22);box-shadow:0 0 18px 4px rgba(217,160,91,0.55);"></div>
      <div style="position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);width:10px;height:10px;border-radius:50%;background:#D9A05B;border:2px solid #0a0a0d;"></div>
    </div>
  `,
});

// Distinct turquoise / cyan pin for temporary drop-pin (address resolution).
// Visually different from project pins (gold) so the user immediately understands
// it is a "what you placed but not yet a project" marker.
const dropPinIcon = L.divIcon({
  className: '',
  iconSize: [32, 40],
  iconAnchor: [16, 38],
  html: `
    <div style="position:relative;width:32px;height:40px;filter:drop-shadow(0 4px 8px rgba(0,0,0,0.5));">
      <svg viewBox="0 0 32 40" width="32" height="40" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="dropGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stop-color="#5EEAD4"/>
            <stop offset="100%" stop-color="#2DD4BF"/>
          </linearGradient>
        </defs>
        <path d="M16 1 C8 1 2 7 2 15 C2 23 11 32 16 38 C21 32 30 23 30 15 C30 7 24 1 16 1 Z"
              fill="url(#dropGrad)" stroke="#0E0E10" stroke-width="2"/>
        <circle cx="16" cy="15" r="5" fill="#0E0E10"/>
      </svg>
      <div style="position:absolute;left:50%;top:38px;width:8px;height:4px;background:radial-gradient(ellipse,rgba(45,212,191,0.55),transparent 70%);transform:translateX(-50%);"></div>
    </div>
  `,
});

const FlyToSelected2D = ({ selectedProject }) => {
  const map = useMap();
  const last = useRef(null);
  useEffect(() => {
    if (!map || !selectedProject) return;
    const c = selectedProject.coords;
    if (!Array.isArray(c) || c.length < 2) return;
    const key = selectedProject.project_id;
    if (last.current === key) return;
    last.current = key;
    map.flyTo([c[0], c[1]], 15, { duration: 1.4, easeLinearity: 0.18 });
  }, [map, selectedProject]);
  return null;
};

const FitAllOnLoad2D = ({ projects, nonce = 0 }) => {
  const map = useMap();
  const last = useRef(undefined);
  useEffect(() => {
    if (!map) return;
    if (last.current === nonce) return;
    last.current = nonce;
    if (!projects.length) return;
    const bounds = L.latLngBounds(projects.map(p => [p.coords[0], p.coords[1]]));
    map.flyToBounds(bounds.pad(0.4), { animate: true, duration: 1.0 });
  }, [map, projects, nonce]);
  return null;
};

const FlyToAddress2D = ({ target }) => {
  const map = useMap();
  const lastKey = useRef(null);
  useEffect(() => {
    if (!map || !target || !target.lat || !target.lon) return;
    const key = `${target.lat}|${target.lon}|${target.nonce || ''}`;
    if (lastKey.current === key) return;
    lastKey.current = key;
    if (target.bbox && target.bbox.length === 4) {
      // bbox = [south, north, west, east]
      const [s, n, w, e] = target.bbox;
      map.flyToBounds([[s, w], [n, e]], { animate: true, duration: 1.2, maxZoom: 16 });
    } else {
      map.flyTo([target.lat, target.lon], 13, { duration: 1.2 });
    }
  }, [map, target]);
  return null;
};

// Fly to a dropped pin (separate from address search to allow live recenter when
// the user repositions the pin by clicking on the map).
const FlyToDropPin2D = ({ pin }) => {
  const map = useMap();
  const lastKey = useRef(null);
  useEffect(() => {
    if (!map || !pin) return;
    const key = `${pin.lat}|${pin.lon}`;
    if (lastKey.current === key) return;
    lastKey.current = key;
    // Only fly when the pin truly changes location, not on every render
    map.flyTo([pin.lat, pin.lon], Math.max(map.getZoom(), 14), { duration: 1.0 });
  }, [map, pin]);
  return null;
};

// Captures empty-map clicks (Leaflet) and forwards lat/lng to parent so it can
// reverse-geocode the position and reposition the drop pin.
const MapClickCatcher = ({ onMapClick }) => {
  useMapEvents({
    click: (e) => {
      if (!onMapClick) return;
      const { lat, lng } = e.latlng;
      onMapClick(lat, lng);
    },
  });
  return null;
};

const Hub2DFallback = ({ pinned, selectedProject, defaultCenter, onMarkerClick, degraded, degradedReason, fitAllNonce, flyTo, onNavigateDetails, onNavigateMinisite, dropPin, onMapClick }) => {
  // Theme: read once at mount (matches HubDashboard convention)
  const mode = (typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'smooth') ? 'smooth' : 'dark';
  const [helpOpen, setHelpOpen] = useState(false);
  const center = pinned.length
    ? [pinned[0].coords[0], pinned[0].coords[1]]
    : [defaultCenter.lat, defaultCenter.lng];

  // Map specific error reasons to user-friendly French copy + actionable guidance
  const REASON_INFO = {
    referrer: {
      label: 'Domaine non autorisé',
      help: "Cette URL n'est pas dans la liste des référents HTTP autorisés de votre clé Google Maps.",
      action: "Ajouter le domaine dans Google Cloud Console",
    },
    tiles_disabled: {
      label: 'Map Tiles API désactivée',
      help: 'L\'API "Map Tiles" (3D Photorealistic) n\'est pas activée sur votre projet GCP.',
      action: "Activer l'API Map Tiles",
    },
    quota: {
      label: 'Quota dépassé',
      help: 'Quota journalier Google Maps atteint ou facturation non activée.',
      action: 'Vérifier la facturation GCP',
    },
    auth: {
      label: 'Authentification refusée',
      help: 'La clé API Google Maps a été refusée par Google.',
      action: 'Vérifier la clé API',
    },
    timeout: {
      label: 'Chargement 3D échoué',
      help: 'Les tuiles 3D n\'ont pas répondu en 5 s — souvent dû à une restriction de domaine ou à la facturation.',
      action: 'Diagnostiquer',
    },
  };
  const reason = degraded ? (REASON_INFO[degradedReason] || REASON_INFO.timeout) : null;

  return (
    <div className="w-full h-full relative" data-testid="hub-3d-map-fallback">
      <MapContainer
        center={center}
        zoom={5}
        minZoom={2}
        scrollWheelZoom
        worldCopyJump
        zoomControl={false}
        attributionControl={false}
        style={{ width: '100%', height: '100%', background: '#0a0a0d' }}
      >
        <TileLayer url={buildTileUrl(mode)} attribution={tileAttribution(mode)} />
        <FitAllOnLoad2D projects={pinned} nonce={fitAllNonce} />
        <FlyToSelected2D selectedProject={selectedProject} />
        <FlyToAddress2D target={flyTo} />
        <FlyToDropPin2D pin={dropPin} />
        <MapClickCatcher onMapClick={onMapClick} />
        {pinned.map((p) => (
          <Marker
            key={p.project_id}
            position={[p.coords[0], p.coords[1]]}
            icon={goldPin}
            eventHandlers={{
              click: () => onMarkerClick?.(p),
              mouseover: (e) => e.target.openPopup(),
              mouseout: (e) => { setTimeout(() => e.target.closePopup(), 220); },
            }}
          >
            <Popup
              autoClose={false}
              closeButton={false}
              closeOnClick={false}
              closeOnEscapeKey={true}
              className="hub-pin-popup"
              offset={[0, -6]}
            >
              <div
                style={{
                  minWidth: 200,
                  padding: '8px 4px',
                  fontFamily: 'Manrope, sans-serif',
                }}
                data-testid={`pin-popup-${p.project_id}`}
              >
                <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: 13, fontWeight: 600, color: '#0E0E10', margin: 0 }}>
                  {p.name}
                </p>
                {p.address && (
                  <p style={{ fontSize: 10.5, color: 'rgba(0,0,0,0.55)', margin: '2px 0 0' }}>
                    {p.address}
                  </p>
                )}
                <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                  <button
                    type="button"
                    onClick={() => onNavigateDetails?.(p)}
                    style={{
                      flex: 1, fontSize: 11, fontWeight: 600, padding: '5px 8px',
                      background: '#0E0E10', color: '#fff', borderRadius: 6,
                      border: 'none', cursor: 'pointer',
                    }}
                    data-testid={`pin-popup-details-${p.project_id}`}
                  >
                    Détails
                  </button>
                  <button
                    type="button"
                    onClick={() => onNavigateMinisite?.(p)}
                    style={{
                      flex: 1, fontSize: 11, fontWeight: 600, padding: '5px 8px',
                      background: '#D9A05B', color: '#000', borderRadius: 6,
                      border: 'none', cursor: 'pointer',
                    }}
                    data-testid={`pin-popup-minisite-${p.project_id}`}
                  >
                    Mini-site
                  </button>
                </div>
              </div>
            </Popup>
          </Marker>
        ))}
        {/* Temporary drop pin (turquoise) — distinct from gold project pins */}
        {dropPin && (
          <Marker
            key="hub-droppin"
            position={[dropPin.lat, dropPin.lon]}
            icon={dropPinIcon}
            zIndexOffset={1000}
          />
        )}
      </MapContainer>
      {/* "2D fallback" status indicator — informative + actionable when degraded */}
      <div className="absolute top-3 left-3 z-[400] flex flex-col gap-2 max-w-[420px]">
        <div
          className="px-2.5 py-1 rounded-md text-[10px] tracking-[0.18em] uppercase font-semibold flex items-center gap-2"
          style={{
            background: 'rgba(10,10,13,0.78)',
            backdropFilter: 'blur(6px)',
            color: degraded ? '#FCA5A5' : 'var(--c-gold)',
            border: `1px solid ${degraded ? 'rgba(239,68,68,0.32)' : 'var(--c-border-accent)'}`,
          }}
          data-testid="hub-map-fallback-badge"
        >
          <span>
            {degraded ? `Map 2D · ${reason?.label || 'Google 3D indisponible'}` : 'Map 2D · Mode standard'}
          </span>
          {degraded && (
            <button
              type="button"
              onClick={() => setHelpOpen((o) => !o)}
              className="ml-1 px-1.5 py-0.5 rounded text-[9px] font-bold pointer-events-auto"
              style={{ background: 'rgba(239,68,68,0.18)', color: '#FCA5A5', border: '1px solid rgba(239,68,68,0.35)' }}
              data-testid="hub-map-help-toggle"
              title="Comment réactiver la carte 3D"
            >
              {helpOpen ? 'FERMER' : 'AIDE ?'}
            </button>
          )}
        </div>

        {degraded && helpOpen && (
          <div
            className="rounded-xl px-3.5 py-3 text-[12px] pointer-events-auto"
            style={{
              background: 'rgba(10,10,13,0.92)',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(239,68,68,0.35)',
              color: 'var(--c-text, #E9E9EE)',
              boxShadow: '0 8px 28px rgba(0,0,0,0.55)',
            }}
            data-testid="hub-map-help-panel"
          >
            <div className="font-semibold mb-1.5" style={{ color: '#FCA5A5' }}>
              {reason?.label}
            </div>
            <p className="mb-2.5 leading-[1.45]" style={{ color: 'rgba(255,255,255,0.78)' }}>
              {reason?.help}
            </p>
            <div className="space-y-1.5 text-[11.5px]" style={{ color: 'rgba(255,255,255,0.7)' }}>
              <div className="font-semibold uppercase tracking-wider text-[10px]" style={{ color: 'var(--c-gold)' }}>Que faire ?</div>
              <ol className="list-decimal pl-4 space-y-1">
                <li>
                  Ouvrir{' '}
                  <a
                    href="https://console.cloud.google.com/apis/credentials"
                    target="_blank"
                    rel="noreferrer"
                    className="underline"
                    style={{ color: 'var(--c-gold)' }}
                  >
                    Google Cloud Console → Identifiants
                  </a>
                </li>
                <li>Sélectionner la clé Maps utilisée par l'application</li>
                {degradedReason === 'referrer' && (
                  <li>
                    Section "Restrictions liées au site" → ajouter le domaine{' '}
                    <code className="px-1 rounded" style={{ background: 'rgba(217,160,91,0.16)', color: 'var(--c-gold)' }}>
                      {typeof window !== 'undefined' ? `${window.location.protocol}//${window.location.host}/*` : '*/*'}
                    </code>
                  </li>
                )}
                {(degradedReason === 'tiles_disabled' || degradedReason === 'timeout') && (
                  <li>
                    Activer dans{' '}
                    <a
                      href="https://console.cloud.google.com/apis/library/tile.googleapis.com"
                      target="_blank"
                      rel="noreferrer"
                      className="underline"
                      style={{ color: 'var(--c-gold)' }}
                    >
                      l'API "Map Tiles"
                    </a>{' '}
                    et{' '}
                    <a
                      href="https://console.cloud.google.com/apis/library/maps-backend.googleapis.com"
                      target="_blank"
                      rel="noreferrer"
                      className="underline"
                      style={{ color: 'var(--c-gold)' }}
                    >
                      "Maps JavaScript API"
                    </a>
                  </li>
                )}
                {degradedReason === 'quota' && (
                  <li>
                    Vérifier la{' '}
                    <a
                      href="https://console.cloud.google.com/billing"
                      target="_blank"
                      rel="noreferrer"
                      className="underline"
                      style={{ color: 'var(--c-gold)' }}
                    >
                      facturation GCP
                    </a>{' '}
                    et les quotas
                  </li>
                )}
                <li>Recharger la page</li>
              </ol>
            </div>
            <div className="mt-2.5 text-[10.5px]" style={{ color: 'rgba(255,255,255,0.45)' }}>
              La carte 2D continue de fonctionner normalement — vos marqueurs et données sont visibles.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Hub3DMap;
